import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  QrCode,
  LogOut,
  Keyboard,
  ShieldCheck,
  Calendar,
  Clock,
  RefreshCw,
  Info,
  ChevronRight,
  Search
} from 'lucide-react';
import '../styles/TicketScanner.css';
import API_BASE_URL from '../config/api';

const TicketScanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get('eventId');
  const [status, setStatus] = useState('IDLE');
  const [message, setMessage] = useState('A aguardar leitura do bilhete...');
  const [details, setDetails] = useState('');
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [ticketCode, setTicketCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef(null);
  const resetTimerRef = useRef(null);
  const isProcessingRef = useRef(false);

  const [showManualModal, setShowManualModal] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const manualInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const rolesStr = localStorage.getItem('roles');
    let isAdmin = false;

    if (rolesStr) {
      try {
        const roles = JSON.parse(rolesStr);
        isAdmin = roles.some(role => role.toLowerCase() === 'admin');
      } catch (e) {
        isAdmin = false;
      }
    }

    if (!token || !isAdmin) {
      navigate('/login?redirect=/scanner');
    }
  }, [navigate]);

  useEffect(() => {
    if (!eventId) {
      const fetchEvents = async () => {
        try {
          const token = localStorage.getItem('token');
          const minEndDate = new Date().toISOString();
          const response = await fetch(`${API_BASE_URL}/api/events?minEndDate=${minEndDate}&hasTicketing=true&isAnnualPass=false`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          if (response.ok) {
            setEvents(data);
          }
        } catch (error) {
          console.error("Failed to load events:", error);
        } finally {
          setLoadingEvents(false);
        }
      };
      fetchEvents();
    } else if (eventId && !selectedEvent) {
      const fetchEventDetails = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          if (response.ok) {
            setSelectedEvent(data);
          }
        } catch (error) {
          console.error("Failed to load event details:", error);
        }
      };
      fetchEventDetails();
    }
  }, [eventId, selectedEvent]);

  useEffect(() => {
    const focusInput = () => {
      const token = localStorage.getItem('token');
      if (inputRef.current && token && !showManualModal) {
        inputRef.current.focus();
      }
    };

    focusInput();
    document.addEventListener('click', focusInput);

    return () => {
      document.removeEventListener('click', focusInput);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, [showManualModal]);

  useEffect(() => {
    if (showManualModal && manualInputRef.current) {
      setTimeout(() => manualInputRef.current.focus(), 100);
    }
  }, [showManualModal]);

  useEffect(() => {
    if (ticketCode.length === 32 && !isProcessingRef.current) {
      processTicket();
    }
  }, [ticketCode]);

  const handleScan = async (e) => {
    if (e) e.preventDefault();
    if (!isProcessingRef.current) {
      processTicket();
    }
  };

  const handleManualSubmit = (e) => {
    if (e) e.preventDefault();
    const cleanedCode = manualInput.replace(/[^a-zA-Z0-9]/g, '');

    if (cleanedCode.length === 32) {
      setTicketCode(cleanedCode);
      setShowManualModal(false);
      setManualInput('');
    } else {
      alert('O código deve ter exatamente 32 caracteres.');
    }
  };

  const processTicket = async () => {
    if (!ticketCode || isProcessingRef.current) return;

    const currentCode = ticketCode.replace(/[\s\x00-\x1F\x7F]/g, '');
    if (currentCode.length < 10) return;

    isProcessingRef.current = true;
    setIsProcessing(true);
    setTicketCode('');

    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }

    setStatus('PROCESSING');
    setMessage('A VALIDAR...');
    setDetails('');

    try {
      const apiUrl = `${API_BASE_URL}/api/tickets/validate/${currentCode}${eventId ? `?eventId=${eventId}` : ''}`;

      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('SUCCESS');
        setMessage(data.message.toUpperCase());
        setDetails(data.buyerName ? `Comprador: ${data.buyerName} | ${data.isAnnual ? `Desporto: ${data.sport}` : `Evento: ${data.eventName}`}` : '');
      } else {
        setStatus('ERROR');
        setMessage(data.message ? data.message.toUpperCase() : 'BILHETE INVÁLIDO');
      }
    } catch (error) {
      console.error("Validation error:", error);
      setStatus('ERROR');
      setMessage('ERRO DE LIGAÇÃO');
      setDetails('Verifique se o servidor está acessível na rede.');
    } finally {
      setIsProcessing(false);
      isProcessingRef.current = false;

      if (inputRef.current && !showManualModal) inputRef.current.focus();

      resetTimerRef.current = setTimeout(() => {
        setStatus('IDLE');

        let idleMsg = 'A aguardar leitura do bilhete...';

        setMessage(idleMsg);
        setDetails('');
        if (inputRef.current && !showManualModal) inputRef.current.focus();
        resetTimerRef.current = null;
      }, 2500);
    }
  };

  const renderStatusIcon = () => {
    switch (status) {
      case 'PROCESSING':
        return <Loader2 className="scanner-icon-main spinning" size={120} />;
      case 'SUCCESS':
        return <CheckCircle2 className="scanner-icon-main success-pop" size={120} />;
      case 'ERROR':
        return <XCircle className="scanner-icon-main error-shake" size={120} />;
      default:
        return <QrCode className="scanner-icon-main idle-pulse" size={120} />;
    }
  };

  if (!eventId) {
    return (
      <div className={`scanner-page status-idle`}>
        <div className="scanner-background">
          <div className="blob blob-1"></div>
          <div className="blob blob-2"></div>
        </div>

        <div className="scanner-content" style={{ zIndex: 10 }}>
          <div className="event-selector-card">
            <div className="event-selector-header">
              <h2>Selecionar Evento</h2>
              <p>Escolha o evento para o qual deseja validar bilhetes hoje.</p>
            </div>

            {loadingEvents ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
                <Loader2 className="spinning" size={48} color="var(--cdp-gold)" />
              </div>
            ) : events.length === 0 ? (
              <div className="no-events-state">
                <Info size={32} />
                <span>Nenhum evento ativo com bilheteira encontrado.</span>
              </div>
            ) : (
              <div className="event-list">
                {events.map(ev => (
                  <button
                    key={ev.id}
                    className="event-item-card"
                    onClick={() => navigate(`?eventId=${ev.id}`)}
                  >
                    <div className="event-item-info">
                      <span className="event-item-title">{ev.title}</span>
                      {ev.opponentName && (
                        <span className="event-item-opponent" style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block' }}>vs {ev.opponentName}</span>
                      )}
                      <div className="event-item-details">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={14} />
                          {new Date(ev.startDateTime).toLocaleDateString('pt-PT')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Clock size={14} />
                          {new Date(ev.startDateTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {ev.sportName && (
                        <span className="event-item-sport-badge">{ev.sportName}</span>
                      )}
                      <ChevronRight size={20} style={{ opacity: 0.3 }} />
                    </div>
                  </button>
                ))}
              </div>
            )}

            <button
              className="scanner-action-btn glass-btn logout"
              style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('roles');
                navigate('/login');
              }}
            >
              <LogOut size={20} />
              <span>Sair da Sessão</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`scanner-page status-${status.toLowerCase()}`}>
      <div className="scanner-background">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
      </div>

      <div className="scanner-content">
        <div className="scanner-status-card glass-card">
          <div className="scanner-icon-container">
            {renderStatusIcon()}
          </div>

          <h1 className="scanner-title">{message}</h1>

          {status === 'IDLE' && eventId && !details && (
            <div className="selected-event-info">
              {selectedEvent ? (
                <>
                  <div className="selected-event-header">
                    <span className="selected-event-title">{selectedEvent.title}</span>
                    {selectedEvent.opponentName && (
                      <span className="selected-event-opponent">vs {selectedEvent.opponentName}</span>
                    )}
                  </div>

                  <div className="selected-event-meta">
                    <div className="selected-event-meta-item">
                      <Calendar size={14} />
                      <span>{new Date(selectedEvent.startDateTime).toLocaleDateString('pt-PT')}</span>
                    </div>
                    <div className="selected-event-meta-item">
                      <Clock size={14} />
                      <span>{new Date(selectedEvent.startDateTime).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>

                  <button
                    className="change-event-btn"
                    onClick={() => {
                      setSelectedEvent(null);
                      navigate('/scanner');
                    }}
                  >
                    <RefreshCw size={12} style={{ marginRight: '6px' }} />
                    Mudar Evento
                  </button>
                </>
              ) : (
                <div className="scanner-details-badge">
                  <span>Evento Selecionado: #{eventId}</span>
                </div>
              )}
            </div>
          )}

          {details && (
            <div className="scanner-details-badge">
              <ShieldCheck size={18} />
              <span>{details}</span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleScan} className="scanner-hidden-form">
        <input
          ref={inputRef}
          type="text"
          value={ticketCode}
          onChange={(e) => setTicketCode(e.target.value)}
          autoFocus
          autoComplete="off"
          disabled={isProcessing}
          inputMode="none"
        />
        <button type="submit">Scan</button>
      </form>

      <div className="scanner-actions">
        <button
          className="scanner-action-btn glass-btn"
          onClick={() => {
            const token = localStorage.getItem('token');
            if (!token) {
              navigate('/login?redirect=/scanner');
              return;
            }
            setShowManualModal(true);
          }}
        >
          <Keyboard size={20} />
          <span>Inserir Manual</span>
        </button>

        <button
          className="scanner-action-btn glass-btn logout"
          onClick={() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('roles');
            navigate('/login');
          }}
        >
          <LogOut size={20} />
          <span>Sair</span>
        </button>
      </div>

      {showManualModal && (
        <div className="manual-modal-overlay" onClick={() => setShowManualModal(false)}>
          <div className="manual-modal-card glass-card" onClick={e => e.stopPropagation()}>
            <div className="manual-modal-header">
              <Keyboard className="modal-icon" size={24} />
              <h2>Inserção Manual</h2>
              <button className="close-modal" onClick={() => setShowManualModal(false)}>
                <XCircle size={24} />
              </button>
            </div>

            <p>Introduza o código de 32 caracteres presente no bilhete.</p>

            <form onSubmit={handleManualSubmit} className="manual-modal-form">
              <input
                ref={manualInputRef}
                type="text"
                placeholder="Código do bilhete..."
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value.toUpperCase())}
                autoComplete="off"
              />
              <div className="modal-actions">
                <button type="button" className="scanner-action-btn cancel" onClick={() => setShowManualModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="scanner-action-btn validate" disabled={!manualInput.trim()}>
                  Validar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketScanner;